import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { AudienceVolumeTrendPoint } from '../../../services/audience-api.service';

interface PlotPoint {
  x: number;
  y: number;
  date: string;
  value: number;
  isFuture: boolean;
}

interface AxisLabel {
  x: number;
  text: string;
  isToday: boolean;
}

interface Pad { left: number; right: number; top: number; bottom: number; }

// Full-size vs mini (collapsed-header preview) coordinate systems.
const FULL = { w: 1000, h: 150, pad: { left: 44, right: 12, top: 10, bottom: 24 } as Pad };
const MINI = { w: 240, h: 46, pad: { left: 3, right: 3, top: 6, bottom: 6 } as Pad };

/**
 * Minimal area sparkline for the global audience-extractions card. Purpose-built for many days (~62)
 * and two states only (past = extracted, future = scheduled): a single area split at "today" reads far
 * better than 62 stacked bars. Past is solid, future is faded/dashed, with a today divider, sparse
 * weekly x-labels, a couple of y-ticks and a light hover readout. Pure SVG, no chart lib.
 *
 * `mini` mode strips all chrome (axes/labels/ticks/hover) and shrinks the box — used as the preview
 * shown in the card header while the full chart is collapsed.
 */
@Component({
  selector: 'app-audience-extractions-sparkline',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './audience-extractions-sparkline.component.html',
  styleUrls: ['./audience-extractions-sparkline.component.scss']
})
export class AudienceExtractionsSparklineComponent implements OnChanges {
  @Input() data: AudienceVolumeTrendPoint[] = [];
  /** Compact header preview: area + line + today divider only (no axes/labels/hover), tiny height. */
  @Input() mini = false;

  points: PlotPoint[] = [];
  private maxValue = 1;
  private todayIndex = -1;

  hoverIndex: number | null = null;

  get vbW(): number { return this.mini ? MINI.w : FULL.w; }
  get vbH(): number { return this.mini ? MINI.h : FULL.h; }
  private get pad(): Pad { return this.mini ? MINI.pad : FULL.pad; }

  get plotTop(): number { return this.pad.top; }
  get plotLeft(): number { return this.pad.left; }
  get plotRight(): number { return this.vbW - this.pad.right; }

  ngOnChanges(): void {
    this.rebuild();
  }

  private rebuild(): void {
    this.hoverIndex = null;
    const today = this.todayString();
    const rows = (this.data ?? []).map(p => ({ date: this.dayString(p.Date), value: Math.max(0, this.num(p.Volumes)) }));
    const n = rows.length;
    this.maxValue = Math.max(1, ...rows.map(r => r.value));

    const pad = this.pad;
    const plotW = this.vbW - pad.left - pad.right;
    const plotH = this.vbH - pad.top - pad.bottom;
    const baseline = pad.top + plotH;

    this.todayIndex = -1;
    this.points = rows.map((r, i) => {
      const x = n <= 1 ? pad.left : pad.left + (plotW * i) / (n - 1);
      const y = baseline - (r.value / this.maxValue) * plotH;
      const isFuture = r.date > today;
      if (!isFuture) {
        this.todayIndex = i; // last non-future index = "today" (or most recent past day)
      }
      return { x, y, date: r.date, value: r.value, isFuture };
    });
  }

  get hasData(): boolean {
    return this.points.some(p => p.value > 0);
  }

  get baselineY(): number {
    return this.vbH - this.pad.bottom;
  }

  /** Boundary point shared by the past and future areas (no visual gap at "today"). */
  private get boundaryIndex(): number {
    if (this.todayIndex >= 0) {
      return this.todayIndex;
    }
    return this.points.length ? 0 : -1;
  }

  get linePath(): string {
    return this.buildLine(this.points);
  }

  get pastAreaPath(): string {
    const k = this.boundaryIndex;
    if (k < 0) {
      return '';
    }
    return this.buildArea(this.points.slice(0, k + 1));
  }

  get futureAreaPath(): string {
    const k = this.boundaryIndex;
    if (k < 0 || k >= this.points.length - 1) {
      return '';
    }
    return this.buildArea(this.points.slice(k)); // starts at boundary → continuous
  }

  get futureLinePath(): string {
    const k = this.boundaryIndex;
    if (k < 0 || k >= this.points.length - 1) {
      return '';
    }
    return this.buildLine(this.points.slice(k));
  }

  get todayX(): number | null {
    if (this.todayIndex < 0 || this.todayIndex >= this.points.length - 1) {
      return null; // no divider if everything is past (or no future portion)
    }
    return this.points[this.todayIndex].x;
  }

  get yTicks(): { y: number; value: number }[] {
    const pad = this.pad;
    const plotH = this.vbH - pad.top - pad.bottom;
    const baseline = pad.top + plotH;
    return [1, 0.5, 0].map(r => ({ y: baseline - r * plotH, value: Math.round(this.maxValue * r) }));
  }

  get xLabels(): AxisLabel[] {
    const n = this.points.length;
    if (!n) {
      return [];
    }
    const step = Math.max(1, Math.ceil(n / 6));
    const labels: AxisLabel[] = [];
    for (let i = 0; i < n; i += step) {
      const p = this.points[i];
      labels.push({ x: p.x, text: this.shortLabel(p.date), isToday: false });
    }
    // Ensure "today" is always marked (replace a nearby label if it collides).
    if (this.todayIndex >= 0 && this.todayIndex < n - 1) {
      const tp = this.points[this.todayIndex];
      const near = labels.find(l => Math.abs(l.x - tp.x) < 40);
      if (near) {
        near.x = tp.x;
        near.text = '';
        near.isToday = true;
      } else {
        labels.push({ x: tp.x, text: '', isToday: true });
      }
    }
    return labels;
  }

  // ── KPIs ────────────────────────────────────────────────────────────────
  get avgPerDay(): number {
    const past = this.points.filter(p => !p.isFuture);
    if (!past.length) {
      return 0;
    }
    return Math.round(past.reduce((acc, p) => acc + p.value, 0) / past.length);
  }

  get peak(): number {
    return this.points.reduce((m, p) => Math.max(m, p.value), 0);
  }

  get scheduledDays(): number {
    return this.points.filter(p => p.isFuture && p.value > 0).length;
  }

  // ── Hover ───────────────────────────────────────────────────────────────
  onMove(event: MouseEvent): void {
    const target = event.currentTarget as SVGElement | null;
    if (this.mini || !target || this.points.length === 0) {
      return;
    }
    const rect = target.getBoundingClientRect();
    // Map the pixel position into viewBox space, then into the plot area (which is inset by pad).
    const xVb = rect.width ? ((event.clientX - rect.left) / rect.width) * this.vbW : this.pad.left;
    const plotW = this.vbW - this.pad.left - this.pad.right;
    this.hoverIndex = this.indexFromRatio((xVb - this.pad.left) / plotW);
  }

  onLeave(): void {
    this.hoverIndex = null;
  }

  /** Nearest point index for a 0..1 horizontal ratio (clamped). Pure → unit-testable. */
  indexFromRatio(ratio: number): number {
    const n = this.points.length;
    if (n <= 1) {
      return 0;
    }
    const clamped = Math.min(1, Math.max(0, ratio));
    return Math.round(clamped * (n - 1));
  }

  get hoverPoint(): PlotPoint | null {
    return this.hoverIndex != null ? this.points[this.hoverIndex] ?? null : null;
  }

  /** Tooltip horizontal position as a % of width (keeps it aligned without JS measuring). */
  get hoverLeftPct(): number {
    const p = this.hoverPoint;
    return p ? (p.x / this.vbW) * 100 : 0;
  }

  formatNumber(value: number): string {
    return value.toLocaleString('it-IT');
  }

  // ── Helpers ─────────────────────────────────────────────────────────────
  private buildLine(pts: PlotPoint[]): string {
    if (pts.length < 2) {
      return '';
    }
    return pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  }

  private buildArea(pts: PlotPoint[]): string {
    if (pts.length < 2) {
      return '';
    }
    const base = this.baselineY.toFixed(1);
    const line = pts.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
    return `${line} L ${pts[pts.length - 1].x.toFixed(1)} ${base} L ${pts[0].x.toFixed(1)} ${base} Z`;
  }

  num(v: number | string | null | undefined): number {
    return Number(v) || 0;
  }

  private todayString(): string {
    return this.dayString(new Date());
  }

  private dayString(value: string | Date): string {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) {
      return String(value).slice(0, 10);
    }
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private shortLabel(date: string): string {
    const [, m, d] = date.split('-');
    return d && m ? `${d}/${m}` : date;
  }
}
